import { Box, Grid } from "@mui/material";
import { createSelector, isFulfilled, isRejected } from "@reduxjs/toolkit";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import TitleWithBackButton from "../../../../common/components/functional/titleWithBackButton/TitleWithBackButton";
import { VariationSelection } from "../../../../common/types/apiTypes";
import useBackendPolling from "../../../../hooks/useBackendPolling";
import useIsLoading from "../../../../hooks/useIsLoading";
import useMessage from "../../../../hooks/useMessage";
import { FormContainer } from "../../../customer/customerManagement/styles";
import {
  fetchAssignmentById,
  fetchTalent,
  resetAssignmentDetails,
  selectTalentById,
} from "../../../talents/talentsSlice";
import { AccessRightContext } from "../../../user/AccessRightProvider";
import { ActionButton } from "../../styles";
import TalentProfileHeader from "../../talentProfileHeader/TalentProfileHeader";
import AssignmentBasedPricing from "./AssignmentBasedPricing/AssignmentBasedPricing";
import AssignmentCancellationBar from "./AssignmentCancellationBar";
import AssignmentSupplementPriceOverridesOverview from "./AssignmentSupplementsPriceOverrides/AssignmentSupplementsPriceOverrides";
import BasicDetails from "./BasicDetails/BasicDetails";
import CustomerContract from "./CustomerContract/CustomerContract";
import OrderAttributes from "./OrderAttributes/OrderAttributes";
import AssignmentSalary from "./Salary/AssignmentSalary";
import SplitAssignmentDialog from "./SplitAssignmentDialog";
import AssignmentSupplements from "./SupplementsToShow/AssignmentSupplements";
import TESContract from "./TESContract/TESContract";
import { SalaryType } from "../../../orders/types/salaryType";

export interface TesContract {
  id: string;
  name: string;
  tesSelections: VariationSelection[];
}

const AssignmentAdditionalInfo = () => {
  const [splitAssignmentDialogOpen, setSplitAssignmentDialogOpen] =
    useState(false);
  const [newAssignmentMode, setNewAssignmentMode] = useState<boolean>(false);
  const [originalAssignmentId, setOriginalAssignmentId] = useState<
    string | null
  >(null);
  const [hideSplitBtnAfterSplit, setHideSplitBtnAfterSplit] = useState(false);
  const [isShift, setIsShift] = useState<boolean>(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { talentId, assignmentId } = useParams();
  const navigate = useNavigate();

  const talent = useAppSelector((state) => selectTalentById(state, talentId!!));
  const talentsSliceStatus = useAppSelector((state) => state.talents.status);

  const selectAssignmentAndLocalized = createSelector(
    [(state) => state.talents.assignment, (state) => state.localized],
    (assignment, localized) => ({ assignment, localized }),
  );

  // Usage in component
  const { assignment } = useAppSelector(selectAssignmentAndLocalized);

  // const { assignment } = useAppSelector((state) => ({
  //   assignment: state.talents.assignment,
  //   localized: state.localized,
  // }));

  const { canEditAssignment, isAdmin } = useContext(AccessRightContext);
  const canEdit = Boolean(
    assignment?.eezyCompanyId && canEditAssignment(assignment?.eezyCompanyId),
  );
  const canEditAssignmentEnd = canEdit && isAdmin;

  const isLoading = useIsLoading(["talents", "customerUsers"]);

  const {
    startPolling,
    stopPolling,
    isTimeout,
    error,
    isPolling,
    clearErrors,
  } = useBackendPolling({
    callback: async () => poller(),
    errorMessage: t("common:actionTimedOut"),
  });

  const poller = async () => {
    dispatch(fetchAssignmentById(assignmentId!!)).then((action) => {
      if (isFulfilled(action)) {
        const payload = action.payload;
        if (payload?.details) {
          setIsShift(payload.details.isShift || false);
          stopPolling();
        }
      }
    });
  };

  useEffect(() => {
    if (!assignmentId) return;

    stopPolling();

    dispatch(fetchAssignmentById(assignmentId)).then((action) => {
      if (isFulfilled(action)) {
        const payload = action.payload;
        if (payload?.details) {
          setIsShift(payload.details.isShift || false);
        } else {
          // backend returns 200 + null
          void startPolling();
        }
      } else if (isRejected(action)) {
        void startPolling();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId]);

  useEffect(() => {
    if (isTimeout || error) {
      clearErrors();
    }
    return () => stopPolling();
    // eslint-disable-next-line
  }, [isTimeout]);

  useEffect(() => {
    return () => {
      dispatch(resetAssignmentDetails());
    };
  }, [dispatch]);

  useEffect(() => {
    if (talentId && !talent && talentsSliceStatus === "idle") {
      dispatch(fetchTalent({ talentId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId]);

  // Take user back to the original assignment view
  const backToOriginalView = () => {
    if (originalAssignmentId) {
      navigate(`/home/talents/${talentId}/assignment/${originalAssignmentId}`);
      setIsShift(false);
      setHideSplitBtnAfterSplit(true);
      setOriginalAssignmentId(null);
      setNewAssignmentMode(false);
    }
  };

  // Take user to the splitted assignment view (new assignment)
  const toSplittedAssignmentView = (id: string) => {
    setOriginalAssignmentId(assignment?.assignmentId);
    navigate(`/home/talents/${talentId}/assignment/${id}`);
    setIsShift(false);
    setNewAssignmentMode(true);
    setSplitAssignmentDialogOpen(false);
    sendMessage(t("SAVING_SUCCESS", { ns: "common" }), "success");
  };

  return (
    <>
      <AssignmentCancellationBar
        cancellation={assignment?.details?.cancellation}
      />
      {talent && <TalentProfileHeader talent={talent} />}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          backgroundColor: newAssignmentMode ? "#E7F8E5" : "default",
          maxWidth: "1500px",
        }}
      >
        <TitleWithBackButton
          title={
            newAssignmentMode
              ? t("talentProfile:newAssignment")
              : t("talentProfile:assignmentAdditionalInfo")
          }
          onBackClick={() => {
            if (newAssignmentMode && originalAssignmentId) {
              backToOriginalView();
            } else {
              hideSplitBtnAfterSplit && setHideSplitBtnAfterSplit(false);
              navigate(`/home/talents/${talentId}/employment/`);
            }
          }}
          containerProps={{
            sx: { gap: "6px" },
          }}
        />

        {!newAssignmentMode &&
          !isShift &&
          !hideSplitBtnAfterSplit &&
          assignment?.details?.start &&
          (!assignment?.details?.end ||
            // Calculate the time difference in hours only if the end date is available
            (new Date(assignment.details.end).getTime() -
              new Date(assignment.details.start).getTime()) /
              (1000 * 60 * 60) >
              24) && (
            <ActionButton
              variant="outlined"
              onClick={() => setSplitAssignmentDialogOpen(true)}
              sx={{ height: "36px" }}
            >
              {t("talentProfile:splitAssignment")}
            </ActionButton>
          )}
      </Box>

      <OverlaySpinner isLoading={isLoading || isPolling} />
      {assignment && assignment?.details?.tesId && (
        <Grid container spacing={2}>
          <Grid item xs={9}>
            <FormContainer
              margin="20px 0px 0px"
              minWidth="800px"
              maxWidth="1500px"
            >
              <BasicDetails
                assignment={assignment}
                canEdit={canEdit}
                canEditAssignmentEnd={canEditAssignmentEnd}
                isShift={isShift}
              />
              <OrderAttributes assignment={assignment} canEdit={canEdit} />
              <CustomerContract
                contractName={assignment?.details?.contractName}
              />
              <TESContract canEdit={canEdit} />
              {assignment.employmentSalaryType === SalaryType.Hourly && (
                <AssignmentSalary canEdit={canEdit} />
              )}
              <AssignmentBasedPricing canEdit={canEdit} />
              <AssignmentSupplements
                assignment={assignment}
                canEdit={canEdit}
              />
              <AssignmentSupplementPriceOverridesOverview
                assignment={assignment}
                canEdit={canEdit}
              />
            </FormContainer>
          </Grid>
        </Grid>
      )}
      <SplitAssignmentDialog
        assignment={assignment}
        splitAssignmentDialogOpen={splitAssignmentDialogOpen}
        setSplitAssignmentDialogOpen={setSplitAssignmentDialogOpen}
        toSplittedAssignmentView={toSplittedAssignmentView}
      />
    </>
  );
};

export default AssignmentAdditionalInfo;
